/**
 * Runtime switch for the current world's lite rendition (see utils/qualityTier).
 * `active` is true only when this device is on the lite tier AND the loaded
 * world ships `settings.lite`; worlds without it render exactly as before.
 * Set by VRViewer's world load; read by the canvas (pixel ratio), the
 * atmosphere (bloom) and the world-game pieces (distance detail).
 */
import { create } from 'zustand';
import { applyLiteUrls, type WorldLiteSettings } from '../utils/qualityTier';

interface LiteState {
  active: boolean;
  settings: WorldLiteSettings | null;
  /** lite URL -> original URL (for anything written back or sent to peers). */
  inverse: Record<string, string> | null;
  setLite: (settings: WorldLiteSettings | null) => void;
}

export const useLiteStore = create<LiteState>((set) => ({
  active: false,
  settings: null,
  inverse: null,
  setLite: (settings) => set({
    active: !!settings,
    settings,
    inverse: settings?.urls ? Object.fromEntries(Object.entries(settings.urls).map(([o, l]) => [l, o])) : null,
  }),
}));

/**
 * The store holds lite URLs on a lite device, but world_assets rows and
 * realtime payloads must always carry the originals, or an owner editing in a
 * headset would quietly swap the desktop world onto its lite files.
 */
export function toOriginalUrls<T>(value: T): T {
  const { active, inverse } = useLiteStore.getState();
  return active && inverse ? applyLiteUrls(value, inverse) : value;
}

/** Peers send originals; a lite device swaps in its variants on receipt. */
export function toLiteUrls<T>(value: T): T {
  const { active, settings } = useLiteStore.getState();
  return active && settings?.urls ? applyLiteUrls(value, settings.urls) : value;
}

/** Defaults for a world that opts in without tuning. */
export const LITE_DEFAULTS = { maxDpr: 1.5, bloom: true, detailDistance: 22 } as const;
